export type EvaluationDomain = "CODES" | "CASE" | "SPANS" | "LOOSE_SPANS";

export type ResolverActionType = "SET_DERIVED_ATTRIBUTE" | "SET_ANSWER" | "ADD_CODE" | "THROW_ERROR" | "MARK_CODE_AS_IDEAL";

export type ResolverCodeType = "PROCEDURE" | "DIAGNOSIS" | "MODIFIER";

/** All valid resolver code types for dropdowns and validation. */
export const RESOLVER_CODE_TYPE_OPTIONS: ResolverCodeType[] = ["DIAGNOSIS", "PROCEDURE", "MODIFIER"];

/** Operand: literal { value } or reference with operandType + type-specific fields. Matches backend OperandDto. */
export interface OperandDto {
    value?: unknown;
    operandType?: "answer" | "derivedAttribute" | "key" | "workItemAttribute" | "aggregate";
    /** Answer set ID; sent to backend. */
    answerSetId?: string;
    /** Question ID for UI/rehydration only; not required by backend. */
    questionId?: number | string;
    /** Answer value ID; used when operandType is answer. */
    answerId?: number | string;
    derivedAttributeName?: string;
    key?: string;
    workItemAttributeName?: string;
    /** aggregate only — aggregate function; currently only "COUNT". */
    operation?: string;
    /** aggregate only — collection to aggregate over: "codes" | "caseAttributes" | "looseSpans". */
    over?: string;
    /** aggregate only — optional filter; when absent all elements are counted. */
    when?: WhenDto;
}

/** When rule AST. Exactly one of the optional fields should be set at root. Backend uses "true" as JSON key. */
export interface WhenDto {
    all?: { conditions: WhenDto[] };
    any?: { conditions: WhenDto[] };
    not?: { condition: WhenDto };
    /** JSON key is "true"; serialized as { "true": true }. Do not use a TS key named "true" for other purposes. */
    true?: boolean;
    eq?: { left: OperandDto; right: OperandDto };
    ne?: { left: OperandDto; right: OperandDto };
    in?: { left: OperandDto; right: unknown[] };
    startsWith?: { left: OperandDto; prefix?: string; postfix?: string; substr?: string };
    endsWith?: { left: OperandDto; prefix?: string; postfix?: string; substr?: string };
    contains?: { left: OperandDto; prefix?: string; postfix?: string; substr?: string };
    gt?: { left: OperandDto; right: OperandDto };
    gte?: { left: OperandDto; right: OperandDto };
    lt?: { left: OperandDto; right: OperandDto };
    lte?: { left: OperandDto; right: OperandDto };
    anyMatch?: { over?: string; when: WhenDto };
    noneMatch?: { over?: string; when: WhenDto };
    allMatch?: { over?: string; when: WhenDto };
}

export interface RulesWrapperDto {
    when?: WhenDto;
}

export interface ResolverActionDto {
    id: number;
    action: ResolverActionType;
    actionDetails?: Record<string, unknown>;
    rules?: RulesWrapperDto;
}

/** Builder-side action shape: in-memory form for the resolver graph UI (id is string). */
export interface ResolverGraphAction {
    id: string;
    action: ResolverActionType;
    actionDetails: Record<string, unknown>;
    rules?: RulesWrapperDto;
}

export interface ResolverEdgeDto {
    id: number;
    toNodeId: number;
    rules?: RulesWrapperDto;
}

export interface ResolverNodeDto {
    id: number;
    name: string;
    evaluates: EvaluationDomain;
    actions: ResolverActionDto[];
    edges: ResolverEdgeDto[];
}

export interface ResolverConfigDto {
    /** Set when config was loaded from or saved to the API. */
    id?: number;
    resolverVersionMajor: number;
    name: string;
    description?: string;
    startNodeId: number;
    nodes: ResolverNodeDto[];
}

/** Attribute shape for resolver input (matches backend AttributeDto). */
export interface ResolverAttributeDto {
    questionId: number;
    answerValueId?: number | null;
    answerDisplayText?: string | null;
    questionLabel: string;
    answerSetId?: number | null;
    answerSetName?: string | null;
}

/** Option for MULTIPLE_CHOICE edit UI (not sent to API). */
export interface ResolverAttributeAnswerOption {
    id: number;
    displayText: string;
}

/** UI entry for an attribute (ResolverAttributeDto + local id for list keys). */
export interface ResolverAttributeEntry extends ResolverAttributeDto {
    id: string;
    /** Options for MULTIPLE_CHOICE edit dropdown; not sent in payload. */
    answerOptions?: ResolverAttributeAnswerOption[];
}

export interface ResolverCodeDto {
    codeType: ResolverCodeType;
    id: number;
    code: string;
    original: boolean;
    sequenceNumber: number;
    units?: number;
    attributes: ResolverAttributeDto[];
    /** Per-code derived attributes for testing (name -> value). */
    derivedAttributes?: Record<string, unknown>;
}

export interface ResolverInputDto {
    codes: ResolverCodeDto[];
    caseAttributes: ResolverAttributeDto[];
    /** Case-level derived attributes for testing (name -> value). */
    derivedAttributes?: Record<string, unknown>;
    workItemData: Record<string, unknown>;
    looseSpans: unknown[];
}

export interface StatelessResolverRequest {
    resolverConfig: ResolverConfigDto;
    input: ResolverInputDto;
}

export interface ResolverValidationError {
    type?: string;
    message?: string;
    [key: string]: unknown;
}

export interface ResolverOutputDto {
    derivedAttributes?: Record<string, unknown>;
    answers?: unknown[];
    codes?: unknown[];
    [key: string]: unknown;
}

export interface ResolverResponse {
    output: ResolverOutputDto;
    validationErrors: ResolverValidationError[];
}

/** Request body for PUT /resolver/config/active (associate or disassociate rubric version with a resolver config). */
export interface SetActiveResolverConfigRequest {
    rubricVersionId: number;
    /** Resolver config ID to associate, or omit/null to disassociate. */
    resolverConfigId?: number | null;
}
